import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Tooltip } from '@mui/material';
import { AddressQueryParams } from '../../services/addressManagementService';

interface FilterPanelProps {
    filters: AddressQueryParams;
    onFilterChange: (newFilters: AddressQueryParams) => void;
    onClearFilters: () => void;
    isLoading?: boolean;
    isOpen: boolean;
    onClose: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
    filters,
    onFilterChange,
    onClearFilters,
    isLoading = false,
    isOpen,
    onClose
}) => {
    // Local state for temporary filter values
    const [tempFilters, setTempFilters] = useState<AddressQueryParams>(filters);

    useEffect(() => {
        setTempFilters(filters);
    }, [isOpen, filters]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        // Handle checkboxes
        if (type === 'checkbox') {
            const isChecked = (e.target as HTMLInputElement).checked;
            setTempFilters({
                ...tempFilters,
                [name]: isChecked ? true : undefined
            });
            return;
        }

        // Handle boolean dropdowns (validation status, Gemini filters, etc.)
        if (name === 'isGoogleValid' || name === 'isRadarValid' || name === 'hasGeminiPreprocessing' || name === 'geminiCorrectionAccepted') {
            setTempFilters({
                ...tempFilters,
                [name]: value === '' ? undefined : value === 'true' ? true : false
            });
            return;
        }

        // Handle other inputs
        setTempFilters({
            ...tempFilters,
            [name]: value === '' ? undefined : value
        });
    };

    const handleDateChange = (date: Date | null, field: 'dateFrom' | 'dateTo') => {
        setTempFilters({
            ...tempFilters,
            [field]: date ? date.toISOString() : undefined
        });
    };

    const handleApplyFilters = () => {
        // Apply filters and reset pagination
        onFilterChange({
            ...tempFilters,
            offset: 0
        });
        onClose();
    };

    const handleClearAllFilters = () => {
        onClearFilters();
        // tempFilters will be reset by the useEffect when filters prop changes
    };

    const GoogleNextActionTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Google Possible Next Action:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>ACCEPT</strong> — The address is accurate and complete. It can be accepted as-is without further interaction from the user.</li>
                <li><strong>CONFIRM</strong> — The address is valid, but the API made minor corrections or inferred parts of it. The user should verify the "recommended" version.</li>
                <li><strong>CONFIRM_ADD_SUBPREMISES</strong> — The address is a multi-unit building, but the specific unit, suite, or apartment number is missing and should be provided.</li>
                <li><strong>FIX</strong> — Significant errors were found in the input (e.g., street or city). The user must edit the address before proceeding.</li>
            </ul>
        </div>
    );

    const RadarGeoconfidenceTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Possible values:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>EXACT</strong> — Precise coordinates for a known address. No further action needed.</li>
                <li><strong>INTERPOLATED</strong> — Estimated coordinates between two known points. The user should verify the location.</li>
                <li><strong>FALLBACK</strong> — Coarse coordinates at postal code or city level. The user should provide a more specific address.</li>
            </ul>
        </div>
    );

    const RadarVerificationTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Possible values:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>VERIFIED</strong> — The address is complete and uniquely matched. It can be accepted as-is.</li>
                <li><strong>PARTIALLY_VERIFIED</strong> — The address partially matched. The user should verify the recommended version.</li>
                <li><strong>AMBIGUOUS</strong> — The address closely matches multiple results. The user should clarify the address before proceeding.</li>
                <li><strong>UNVERIFIED</strong> — The address could not be verified. The user must edit the address before proceeding.</li>
            </ul>
        </div>
    );

    const DestinationTypeTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>USPS Destination Types:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>F — Firm</strong> — The address belongs to a specific company or organization at that location (e.g., a named business building)</li>
                <li><strong>G — General Delivery</strong> — A temporary mail service for people without a permanent address, typically at a post office</li>
                <li><strong>H — High-rise</strong> — A multi-unit building such as an apartment complex, condo, or office tower where a unit number is expected</li>
                <li><strong>P — PO Box</strong> — A locked box at a post office, not a physical delivery location</li>
                <li><strong>R — Rural Route</strong> — A mail route in a rural area, typically without a traditional street address</li>
                <li><strong>S — Street</strong> — A standard street address with a house or building number on a named street</li>
            </ul>
        </div>
    );

    const GeminiStatusTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Gemini Preprocessing Status:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>Success</strong> — Gemini successfully processed and validated the address</li>
                <li><strong>Failed</strong> — Gemini processing failed due to an error</li>
                <li><strong>Disabled</strong> — Gemini preprocessing was disabled for this address</li>
                <li><strong>Rejected</strong> — Gemini preprocessing was rejected based on criteria</li>
                <li><strong>Error</strong> — An error occurred during Gemini preprocessing</li>
            </ul>
        </div>
    );

    return (
        <>
            {/* Overlay */}
            <div
                className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${!isOpen ? "d-none" : ""}`}
                onClick={onClose}
                style={{ zIndex: 1040 }}
            />

            {/* Sidebar */}
            <div
                className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isOpen ? "translate-end d-none" : ""}`}
                style={{ width: "850px", zIndex: 1045 }}
            >
                <div className="d-flex flex-column h-100">
                    <div className="p-3 border-bottom bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-primary">
                                <i className="bi bi-funnel-fill me-2"></i>Filter Addresses
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            />
                        </div>
                    </div>

                    <div className="p-3 flex-grow-1">
                        {/* Trace IDs Search */}
                        <div className="mb-4">
                            <label className="form-label fw-bold small text-muted">Trace IDs</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-search"></i></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by Trace IDs (comma-separated)..."
                                    name="traceIds"
                                    value={tempFilters.traceIds || ''}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        {/* Full Address Search */}
                        <div className="mb-4">
                            <label className="form-label fw-bold small text-muted">Full Address</label>
                            <div className="input-group">
                                <span className="input-group-text"><i className="bi bi-search"></i></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by full address..."
                                    name="fullAddress"
                                    value={tempFilters.fullAddress || ''}
                                    onChange={handleInputChange}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        {/* Coordinate Source */}
                        <div className="mb-4">
                            <label htmlFor="coordinateSource" className="form-label fw-bold small text-muted">Coordinate Source</label>
                            <select
                                className="form-select"
                                id="coordinateSource"
                                name="coordinateSource"
                                value={tempFilters.coordinateSource || ''}
                                onChange={handleInputChange}
                                disabled={isLoading}
                            >
                                <option value="">All Sources</option>
                                <option value="GOOGLE">GOOGLE</option>
                                <option value="RADAR">RADAR</option>
                                <option value="UBER">UBER</option>
                                <option value="AdminPortal">ADMIN_PORTAL</option>
                                <option value="AddressCorrectionPortal">ADDRESS_CORRECTION_PORTAL</option>
                                <option value="NONE">NONE</option>
                            </select>
                        </div>

                        <hr className="my-4" />

                        <div className="vstack gap-4">
                            {/* Validation Status */}
                            <div>
                                <label className="form-label fw-bold small text-muted mb-2">Validation Status</label>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label htmlFor="isGoogleValid" className="form-label small text-muted d-block">Google Validation</label>
                                        <select
                                            className="form-select"
                                            id="isGoogleValid"
                                            name="isGoogleValid"
                                            value={tempFilters.isGoogleValid === undefined ? '' : tempFilters.isGoogleValid ? 'true' : 'false'}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Valid</option>
                                            <option value="false">Invalid</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="isRadarValid" className="form-label small text-muted d-block">Radar Validation</label>
                                        <select
                                            className="form-select"
                                            id="isRadarValid"
                                            name="isRadarValid"
                                            value={tempFilters.isRadarValid === undefined ? '' : tempFilters.isRadarValid ? 'true' : 'false'}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Valid</option>
                                            <option value="false">Invalid</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Validation Attributes */}
                            <div>
                                <label className="form-label fw-bold small text-muted mb-2">Attributes</label>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label htmlFor="googlePossibleNextAction" className="form-label small text-muted d-block">
                                            Google Next Action
                                            <Tooltip title={GoogleNextActionTooltip} arrow placement="top">
                                                <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                            </Tooltip>
                                        </label>
                                        <select
                                            className="form-select"
                                            id="googlePossibleNextAction"
                                            name="googlePossibleNextAction"
                                            value={tempFilters.googlePossibleNextAction || ''}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Actions</option>
                                            <option value="ACCEPT">ACCEPT</option>
                                            <option value="CONFIRM">CONFIRM</option>
                                            <option value="CONFIRM_ADD_SUBPREMISES">CONFIRM_ADD_SUBPREMISES</option>
                                            <option value="FIX">FIX</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="radarVerificationStatus" className="form-label small text-muted d-block">
                                            Radar Verification Status
                                            <Tooltip title={RadarVerificationTooltip} arrow placement="top">
                                                <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                            </Tooltip>
                                        </label>
                                        <select
                                            className="form-select"
                                            id="radarVerificationStatus"
                                            name="radarVerificationStatus"
                                            value={tempFilters.radarVerificationStatus || ''}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="VERIFIED">VERIFIED</option>
                                            <option value="PARTIALLY_VERIFIED">PARTIALLY_VERIFIED</option>
                                            <option value="AMBIGUOUS">AMBIGUOUS</option>
                                            <option value="UNVERIFIED">UNVERIFIED</option>
                                        </select>
                                    </div>
                                    
                                </div>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label htmlFor="radarGeoconfidence" className="form-label small text-muted d-block">
                                            Radar Geoconfidence
                                            <Tooltip title={RadarGeoconfidenceTooltip} arrow placement="top">
                                                <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                            </Tooltip>
                                        </label>
                                        <select
                                            className="form-select"
                                            id="radarGeoconfidence"
                                            name="radarGeoconfidence"
                                            value={tempFilters.radarGeoconfidence || ''}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Types</option>
                                            <option value="EXACT">EXACT</option>
                                            <option value="INTERPOLATED">INTERPOLATED</option>
                                            <option value="FALLBACK">FALLBACK</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="geoFenceConfidence" className="form-label small text-muted d-block">Geofence Confidence (Google and Radar)</label>
                                        <select
                                            className="form-select"
                                            id="geoFenceConfidence"
                                            name="geoFenceConfidence"
                                            value={tempFilters.geoFenceConfidence || ''}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Levels</option>
                                            <option value="HIGH">HIGH</option>
                                            <option value="MEDIUM">MEDIUM</option>
                                            <option value="LOW">LOW</option>
                                            <option value="NONE">NONE</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Address Classification */}
                            <div>
                                <label className="form-label fw-bold small text-muted mb-2">Address Classification</label>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label htmlFor="isApartment" className="form-label small text-muted d-block">Is Apartment</label>
                                        <select
                                            className="form-select"
                                            id="isApartment"
                                            name="isApartment"
                                            value={tempFilters.isApartment === undefined ? '' : tempFilters.isApartment ? 'true' : 'false'}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="destinationType" className="form-label small text-muted d-block">
                                            Destination Type
                                            <Tooltip title={DestinationTypeTooltip} arrow placement="top">
                                                <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                            </Tooltip>
                                        </label>
                                        <select
                                            className="form-select"
                                            id="destinationType"
                                            name="destinationType"
                                            value={tempFilters.destinationType || ''}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Types</option>
                                            <option value="F">F — Firm</option>
                                            <option value="G">G — General Delivery</option>
                                            <option value="H">H — High-rise</option>
                                            <option value="P">P — PO Box</option>
                                            <option value="R">R — Rural Route</option>
                                            <option value="S">S — Street</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* POD Availability */}
                            <div>
                                <label className="form-label fw-bold small text-muted mb-2">
                                    POD Availability
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '0.85rem' }}>
                                                <strong>Proof of Delivery (POD) Images:</strong>
                                                <ul className="mb-0 ps-3">
                                                    <li><strong>Has PODs</strong> — Only show addresses with at least one POD image</li>
                                                    <li><strong>No PODs</strong> — Only show addresses without any POD images</li>
                                                    <li><strong>All</strong> — Show all addresses regardless of POD availability</li>
                                                </ul>
                                            </div>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                    </Tooltip>
                                </label>
                                <div className="row g-2">
                                    <div className="col-12">
                                        <label htmlFor="hasPod" className="form-label small text-muted d-block">Filter by POD Images</label>
                                        <select
                                            className="form-select"
                                            id="hasPod"
                                            name="hasPod"
                                            value={tempFilters.hasPod === undefined ? '' : tempFilters.hasPod ? 'true' : 'false'}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Addresses</option>
                                            <option value="true">Has PODs</option>
                                            <option value="false">No PODs</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Gemini Preprocessing Filters */}
                            <div>
                                <label className="form-label fw-bold small text-muted mb-2">
                                    Gemini AI Preprocessing
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '0.85rem' }}>
                                                <strong>Gemini AI Address Preprocessing:</strong>
                                                <p className="mb-0">Filter addresses by Gemini AI preprocessing status, whether corrections were made, and if those corrections were accepted.</p>
                                            </div>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                    </Tooltip>
                                </label>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label htmlFor="hasGeminiPreprocessing" className="form-label small text-muted d-block">Has Gemini Logs</label>
                                        <select
                                            className="form-select"
                                            id="hasGeminiPreprocessing"
                                            name="hasGeminiPreprocessing"
                                            value={tempFilters.hasGeminiPreprocessing === undefined ? '' : tempFilters.hasGeminiPreprocessing ? 'true' : 'false'}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Addresses</option>
                                            <option value="true">With Gemini Preprocessing</option>
                                            <option value="false">Without Gemini Preprocessing</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="geminiStatus" className="form-label small text-muted d-block">
                                            Gemini Status
                                            <Tooltip title={GeminiStatusTooltip} arrow placement="top">
                                                <i className="bi bi-info-circle ms-1" style={{ cursor: 'help' }}></i>
                                            </Tooltip>
                                        </label>
                                        <select
                                            className="form-select"
                                            id="geminiStatus"
                                            name="geminiStatus"
                                            value={tempFilters.geminiStatus || ''}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="Success">Success</option>
                                            <option value="Failed">Failed</option>
                                            <option value="Disabled">Disabled</option>
                                            <option value="Rejected">Rejected</option>
                                            <option value="Error">Error</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="row g-2">
                                    <div className="col-12">
                                        <label htmlFor="geminiCorrectionAccepted" className="form-label small text-muted d-block">Correction Accepted</label>
                                        <select
                                            className="form-select"
                                            id="geminiCorrectionAccepted"
                                            name="geminiCorrectionAccepted"
                                            value={tempFilters.geminiCorrectionAccepted === undefined ? '' : tempFilters.geminiCorrectionAccepted ? 'true' : 'false'}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                        >
                                            <option value="">All Addresses</option>
                                            <option value="true">Correction Accepted</option>
                                            <option value="false">Correction Not Accepted</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Date Range */}
                            <div>
                                <label className="form-label fw-bold small text-muted">Created Date Range</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                                    <div className="flex-grow-1">
                                        <DatePicker
                                            selected={tempFilters.dateFrom ? new Date(tempFilters.dateFrom) : null}
                                            onChange={(date: Date | null) => handleDateChange(date, 'dateFrom')}
                                            selectsStart
                                            startDate={tempFilters.dateFrom ? new Date(tempFilters.dateFrom) : undefined}
                                            endDate={tempFilters.dateTo ? new Date(tempFilters.dateTo) : undefined}
                                            className="form-control rounded-0 rounded-end"
                                            placeholderText="Start Date"
                                            dateFormat="yyyy-MM-dd"
                                            disabled={isLoading}
                                            wrapperClassName="w-100"
                                        />
                                    </div>
                                </div>
                                <div className="input-group mt-2">
                                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                                    <div className="flex-grow-1">
                                        <DatePicker
                                            selected={tempFilters.dateTo ? new Date(tempFilters.dateTo) : null}
                                            onChange={(date: Date | null) => handleDateChange(date, 'dateTo')}
                                            selectsEnd
                                            startDate={tempFilters.dateFrom ? new Date(tempFilters.dateFrom) : undefined}
                                            endDate={tempFilters.dateTo ? new Date(tempFilters.dateTo) : undefined}
                                            minDate={tempFilters.dateFrom ? new Date(tempFilters.dateFrom) : undefined}
                                            className="form-control rounded-0 rounded-end"
                                            placeholderText="End Date"
                                            dateFormat="yyyy-MM-dd"
                                            disabled={isLoading}
                                            wrapperClassName="w-100"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3 border-top bg-light">
                        <div className="d-grid gap-2">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleClearAllFilters}
                                disabled={isLoading}
                            >
                                <i className="bi bi-x-circle me-1"></i>Clear All Filters
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleApplyFilters}
                                disabled={isLoading}
                            >
                                <i className="bi bi-search me-1"></i>Search / Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FilterPanel;
